import * as React from "react";
import { connect } from "react-redux";
import { ApiClient } from "../../../lib/api";
import { MainComponentContainer } from "../../../shared/MainComponentContainer";
import { RootState } from "../../../store/rootReducer";
import { Row, Col, Skeleton, message } from "antd";
import styled from "styled-components";
import { ReactComponent as IconRebook } from "../../../assets/icons/icon_rebook_white.svg";
import { ReactComponent as IconWaybill } from "../../../assets/icons/icon_waybills_white.svg";
import { ReactComponent as IconInvoice } from "../../../assets/icons/icon_invoice_white.svg";
import { ForwardQuickActionButton } from "../../../shared/ForwardQuickActionbutton";
import { OrderDetailsHeader } from "./OrderDetailsHeader";
import { Map } from "./Map";
import { Order } from "../../../lib/model/Order";
import { Shipper } from "../../../lib/model/Shipper";
import { RouteComponentProps } from "react-router";
import {
  priceString,
  displayStringForExtraType
} from "../../../lib/formatters";
import { push } from "connected-react-router";
import { interval, Subscription } from "rxjs";
import {
  OrderStatus,
  hasStatusReachedStatus
} from "../../../lib/model/OrderStatus";
import { LiveTrackingLocation } from "../../../lib/model/LiveTrackingLocation";
import { Tour } from "../../../lib/model/Tour";
import { TourDetailsTable } from "./TourDetailsTable";
import { ETA } from "../../../lib/model/ETA";
import { OrderPriceList } from "./OrderPriceList";
import { ExtraType } from "../../../lib/model/ExtraType";

const GreyBox = styled.div`
  background-color: #f7f7f7;
  border: 1px solid #e8e8e8;
  height: 126px;
  width: 100%;
  padding: 17px 22px;

  @media only screen and (max-width: 876px) {
    .ant-row-flex.ant-row-flex-middle {
      align-items: start;
    }
  }

  @media only screen and (max-width: 768px) {
    text-align: center;

    .ant-row-flex.ant-row-flex-middle {
      align-items: center;
    }
  }

  @media only screen and (max-width: 576px) {
    .ant-row-flex.ant-row-flex-middle {
      align-items: start;
    }
  }
`;

const Frame = styled.div`
  border: 1px solid #e8e8e8;
  width: 100%;
  padding: 20px 25px;
`;

const PriceTitleLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  color: #061a32;
`;

const PriceLabel = styled.span`
  font-size: 28px;
  font-weight: 400;
  color: #061a32;
`;

const HintLabel = styled.span`
  font-size: 11px;
  font-weight: 300;
  color: rgba(6, 26, 50, 0.56);
  display: inline-block;
`;

const ExtrasLabel = styled.span`
  font-size: 11px;
  font-weight: 300;
  line-height: 1.36;
  color: #061a32;
  display: inline-block;
`;

const client = new ApiClient();

type State = {
  order?: Order;
  tour?: Tour;
  shipper?: Shipper;
  liveLocation?: LiveTrackingLocation;
  liveLocationIntervalSubscription?: Subscription;
  ETAs?: ETA[];
  ETAIntervalSubscription?: Subscription;
  orderUpdateSubscription?: Subscription;
};

const mapStateToProps = (
  state: RootState,
  ownProps: RouteComponentProps<{ orderId: string }>
) => ({
  orderId: ownProps.match.params.orderId
});

const dispatchProps = {
  push
};

type Props = ReturnType<typeof mapStateToProps> & typeof dispatchProps;

class OrderDetailsContainer extends React.Component<Props, State> {
  readonly state: State = {};
  render() {
    const { order, shipper, tour, liveLocation, ETAs } = this.state;
    const booked =
      !!shipper && !!order && order.shipperMemberCognitoId
        ? shipper.members.find(
            m => m.cognitoId === order.shipperMemberCognitoId
          )
        : undefined;
    return (
      <MainComponentContainer>
        <Row type="flex">
          <Col span={24} style={{ width: "100%", height: 480 }}>
            <Map
              route={tour ? tour.stops.map(t => t.address) : []}
              liveLocation={liveLocation}
            />
          </Col>
          <Col span={24}>
            {/* Price and Action Button Section */}
            <Row
              type="flex"
              style={{ margin: 0, padding: "20px 15px" }}
              gutter={30}
            >
              <Skeleton active loading={order === undefined} title={false}>
                {order && (
                  <>
                    <Col span={7} md={7} sm={24} xs={24}>
                      <GreyBox>
                        <PriceTitleLabel>
                          {hasStatusReachedStatus(
                            order.status,
                            OrderStatus.ReadyToInvoice
                          )
                            ? "Gesamtpreis"
                            : "Grundpreis"}
                        </PriceTitleLabel>
                        <br />
                        <PriceLabel>
                          {priceString(order!.fullPrice.net)}
                        </PriceLabel>
                        <br />
                        <HintLabel>
                          Durch Wartezeiten können Zusatzkosten entstehen.
                        </HintLabel>
                      </GreyBox>
                    </Col>
                    <Col span={17} md={17} sm={24} xs={24}>
                      <GreyBox>
                        <Row
                          type="flex"
                          justify="space-between"
                          align="middle"
                          style={{ height: "100%" }}
                        >
                          <Col span={6} xs={8} md={6}>
                            <ForwardQuickActionButton
                              isDisabled={true}
                              onClick={() => {}}
                              icon={IconRebook}
                              title="Tour erneut buchen"
                            />
                          </Col>
                          <Col span={6} xs={8} md={6}>
                            <ForwardQuickActionButton
                              isDisabled={
                                !hasStatusReachedStatus(
                                  order.status,
                                  OrderStatus.ReadyToInvoice
                                ) || order.waybills.length < 1
                              }
                              icon={IconWaybill}
                              title="Frachtpapiere"
                              href={order.waybillPdfUrl || order.waybills[0]}
                              target="_blank"
                            />
                          </Col>
                          <Col span={6} xs={8} md={6}>
                            <ForwardQuickActionButton
                              isDisabled={order.invoiceId === undefined}
                              onClick={() =>
                                this.props.push(`/invoices/${order.invoiceId}`)
                              }
                              icon={IconInvoice}
                              title="Rechnung aufrufen"
                            />
                          </Col>
                        </Row>
                      </GreyBox>
                    </Col>
                  </>
                )}
              </Skeleton>
            </Row>
          </Col>
          <Col span={24}>
            {/* Detailed Progress Section */}
            <Row type="flex" style={{ margin: 0, padding: "0 30px 30px 30px" }}>
              <Skeleton active loading={order === undefined} title={false}>
                {order && (
                  <Col span={24}>
                    <Frame>
                      <Row type="flex">
                        <Col span={24} style={{ marginBottom: 12 }}>
                          <OrderDetailsHeader
                            id={order.id}
                            internalReference={order.internalReference || "-"}
                            date={order.startTime}
                            status={order.status}
                            bookingMember={
                              booked && `${booked.firstName} ${booked.lastName}`
                            }
                          />
                        </Col>
                        <Skeleton
                          active
                          loading={tour === undefined}
                          title={false}
                        >
                          {tour && (
                            <Col span={24} style={{ overflow: "auto" }}>
                              <TourDetailsTable tour={tour} ETAs={ETAs || []} />
                            </Col>
                          )}
                        </Skeleton>
                        <Col
                          span={16}
                          sm={16}
                          xs={24}
                          style={{ marginTop: 20 }}
                        >
                          {order.comment && (
                            <>
                              <ExtrasLabel>
                                Kommentar: {order.comment}
                              </ExtrasLabel>
                              <br />
                            </>
                          )}
                          {order.extras.length > 0 && (
                            <>
                              <ExtrasLabel>Gebuchte Fahrtoptionen:</ExtrasLabel>
                              <br />
                              <ExtrasLabel>
                                {order.extras
                                  .filter(
                                    e =>
                                      e.type !== ExtraType.LargeWindowDiscount
                                  )
                                  .map(e => displayStringForExtraType(e.type))
                                  .join(", ")}
                              </ExtrasLabel>
                            </>
                          )}
                        </Col>
                        <Col span={8} sm={8} xs={24} style={{ marginTop: 20 }}>
                          <OrderPriceList order={order} />
                        </Col>
                      </Row>
                    </Frame>
                  </Col>
                )}
              </Skeleton>
            </Row>
          </Col>
        </Row>
      </MainComponentContainer>
    );
  }
  componentDidMount = () => {
    this.loadOrder();
    this.loadShipper();
    this.startOrderUpdate();
  };
  componentWillUnmount = () => {
    if (this.state.liveLocationIntervalSubscription) {
      this.state.liveLocationIntervalSubscription.unsubscribe();
    }
    if (this.state.ETAIntervalSubscription) {
      this.state.ETAIntervalSubscription.unsubscribe();
    }
    if (this.state.orderUpdateSubscription) {
      this.state.orderUpdateSubscription.unsubscribe();
    }
  };
  startOrderUpdate = () => {
    const orderUpdateUpdateSource = interval(30000);
    const subscription = orderUpdateUpdateSource.subscribe(() =>
      this.loadOrder()
    );
    this.setState({ orderUpdateSubscription: subscription });
  };
  loadOrder = async () => {
    try {
      const order = await client.fetchOrder(this.props.orderId);
      if (order.status === OrderStatus.InProgress) {
        this.loadLatestLiveLocation(order.tours[0]);
        this.startLiveLocationUpdates();
        this.startETAUpdates();
      }
      this.loadLatestETA(order.tours[0]);
      this.setState({ order: order });
      this.loadTour(order.tours[0]);
    } catch (error) {
      message.error(error.message);
    }
  };
  loadShipper = async () => {
    try {
      const shipper = await client.fetchShipperAccount();
      this.setState({ shipper: shipper });
    } catch (error) {
      console.log(error);
    }
  };
  loadTour = async (tourId: string) => {
    try {
      const tour = await client.fetchTour(tourId);
      this.setState({ tour });
    } catch (error) {
      message.error(error.message);
    }
  };
  startLiveLocationUpdates = () => {
    if (!!this.state.liveLocationIntervalSubscription) return;
    const liveTrackingUpdateSource = interval(3000);
    const subscription = liveTrackingUpdateSource.subscribe(() =>
      this.loadLatestLiveLocation()
    );
    this.setState({ liveLocationIntervalSubscription: subscription });
  };
  loadLatestLiveLocation = async (tourId?: string) => {
    const param =
      tourId ||
      (this.state.order &&
        this.state.order.tours.length > 0 &&
        this.state.order.tours[0]);
    if (param) {
      try {
        const location = await client.fetchLiveTrackingLocation(param);
        this.setState({ liveLocation: location });
      } catch (error) {
        if (error.response.status === 404) {
          console.log("Livetracking not available");
          // message.warning("Livetracking nicht verfügbar");
        } else {
          console.error(error);
          // message.error(error.message);
        }
      }
    }
  };
  startETAUpdates = () => {
    if (!!this.state.ETAIntervalSubscription) return;
    const ETAUpdateSource = interval(10000);
    const subscription = ETAUpdateSource.subscribe(() => {
      this.loadLatestETA();
    });
    this.setState({ ETAIntervalSubscription: subscription });
  };
  loadLatestETA = async (tourId?: string) => {
    const param = tourId || (this.state.tour && this.state.tour.id);
    if (param) {
      try {
        const eta = await client.fetchETA(param);
        this.setState({ ETAs: eta });
      } catch (error) {
        if (error && error.response && error.response.status === 404) {
          console.log("ETAs not available");
        } else if (error) {
          // message.error(error.message);
          console.error(error);
        }
      }
    }
  };
}

export default connect(
  mapStateToProps,
  dispatchProps
)(OrderDetailsContainer);
